import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    Grid,
    Tooltip,
    IconButton
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import { AIService, PromptTemplate } from '../types';
import {
    fetchAIServices,
    getMarketplaces,
    getProducts,
    getResearchGenerationsForProduct,
    getPromptTemplates,
} from '../services/api';
import ResearchHistoryDialog from '../components/research/ResearchHistoryDialog';
import ResearchDetailsContent from '../components/research/ResearchDetailsContent';
import AIGuideDialog from '../components/AIGuideDialog';
import { useResearch } from '../hooks/useResearch';
import PromptViewerDialog from "../components/PromptViewerDialog";

const ResearchTestPage: React.FC = () => {
    // --- Состояния UI ---
    const [selectedProduct, setSelectedProduct] = useState<number | ''>('');
    const [selectedMarketplace, setSelectedMarketplace] = useState<number | ''>('');
    const [selectedAIServiceCode, setSelectedAIServiceCode] = useState<string>('perplexity_sonar');
    const [selectedPromptId, setSelectedPromptId] = useState<number | ''>('');
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [isAIGuideOpen, setIsAIGuideOpen] = useState(false);
    const [viewedAIService, setViewedAIService] = useState<AIService | null>(null);
    const [isPromptViewerOpen, setIsPromptViewerOpen] = useState(false);
    const [viewedPrompt, setViewedPrompt] = useState<{ name: string; template: string } | null>(null);
    const [selectedReferenceImageUrl, setSelectedReferenceImageUrl] = useState<string | null>(null);

    // --- Запросы к API ---
    const { data: products = [] } = useQuery(['products'], getProducts);
    const { data: marketplaces = [] } = useQuery(['marketplaces'], getMarketplaces);
    const { data: promptTemplates = [], isLoading: isLoadingPromptTemplates } = useQuery<PromptTemplate[]>(['promptTemplates'], () => getPromptTemplates());
    const { data: aiServices = [] } = useQuery<AIService[]>(['aiServices', 'research'], () => fetchAIServices('research'));

    const { data: researchHistoryData = [], isLoading: isLoadingHistory, isError: isErrorHistory } = useQuery({
        queryKey: ['researchHistory', selectedProduct],
        queryFn: () => getResearchGenerationsForProduct(selectedProduct as number),
        enabled: !!selectedProduct,
    });

    useEffect(() => {
        if (promptTemplates.length > 0 && !selectedPromptId && !isLoadingPromptTemplates) {
            const availablePrompts = promptTemplates.filter(t => t.task_type === 'advanced_research' && t.is_active);

            if (availablePrompts.length === 1) {
                setSelectedPromptId(availablePrompts[0].id);
                console.log(`🎯 Auto-selected prompt: "${availablePrompts[0].name}"`);
            }
        }
    }, [promptTemplates, selectedPromptId, isLoadingPromptTemplates]);

    const fullSelectedProduct = useMemo(() => {
        return products?.find(p => p.id === selectedProduct);
    }, [products, selectedProduct]);

    const selectedAIService = useMemo(() => {
        return aiServices.find(s => s.code === selectedAIServiceCode);
    }, [aiServices, selectedAIServiceCode]);

    const selectedMarketplaceCode = useMemo(() => {
        return marketplaces.find(m => m.id === selectedMarketplace)?.code;
    }, [marketplaces, selectedMarketplace]);

    const selectedPromptTemplate = useMemo(() => {
        if (!promptTemplates || !selectedPromptId) return null;
        return promptTemplates.find(p => p.id === selectedPromptId);
    }, [promptTemplates, selectedPromptId]);

    const {
        researchResult,
        researchError,
        isRunning,
        startResearchMutation,
        handleStartResearch,
        handleViewResearch
    } = useResearch({
        selectedProductId: selectedProduct,
        selectedAIServiceCode,
        marketplaceCode: selectedMarketplaceCode,
        promptTemplateId: selectedPromptId,
        referenceImageUrl: selectedReferenceImageUrl,
    });

    // --- Обработчики событий ---
    const handleViewAIService = (service: AIService | null) => {
        setViewedAIService(service);
        setIsAIGuideOpen(true);
    };

    const handleViewPromptTemplate = (prompt: PromptTemplate | null) => {
        if (prompt) {
            setViewedPrompt({ name: prompt.name, template: prompt.template });
            setIsPromptViewerOpen(true);
        }
    };

    const availablePrompts = useMemo(() => {
        return promptTemplates.filter(t => t.task_type === 'advanced_research' && t.is_active);
    }, [promptTemplates]);

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <>
                <Typography variant="h4" component="h1" gutterBottom>Product Research Test</Typography>
                <Typography color="text.secondary" sx={{mb: 2}}>Test the research system with dynamic prompts.</Typography>

                <Grid container spacing={3} sx={{mb: 3}}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>1. Product & Reference</Typography>
                                <FormControl fullWidth size="small" sx={{mb: 2}}>
                                    <InputLabel>Product</InputLabel>
                                    <Select
                                        value={selectedProduct}
                                        label="Product"
                                        onChange={e => {
                                            setSelectedProduct(e.target.value as number);
                                            setSelectedReferenceImageUrl(null);
                                        }}
                                        disabled={isRunning}
                                    >
                                        {products.map((p) => (
                                            <MenuItem key={p.id} value={p.id}>{p.name} {p.brand && `(${p.brand})`}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {fullSelectedProduct && (
                                    <Box sx={{ mt: 1, pointerEvents: isRunning ? 'none' : 'auto', opacity: isRunning ? 0.5 : 1 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Select Reference Image (Optional):
                                        </Typography>
                                        {(fullSelectedProduct.images && fullSelectedProduct.images.length > 0) ? (
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 150, overflowY: 'auto', p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                                {fullSelectedProduct.images.map((url) => (
                                                    <Box
                                                        key={url}
                                                        sx={{
                                                            width: 80,
                                                            height: 80,
                                                            cursor: 'pointer',
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            border: `3px solid ${url === selectedReferenceImageUrl ? '#1976d2' : 'transparent'}`,
                                                            '&:hover': {
                                                                borderColor: url === selectedReferenceImageUrl ? '#1976d2' : '#90caf9',
                                                            },
                                                            transition: 'border-color 0.2s',
                                                        }}
                                                        onClick={() => setSelectedReferenceImageUrl(url)}
                                                    >
                                                        <img
                                                            src={url}
                                                            alt="Product reference"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography color="text.secondary" variant="body2">
                                                This product has no images.
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                <FormControl fullWidth size="small" sx={{mt: 2}}>
                                    <InputLabel>Marketplace (Optional)</InputLabel>
                                    <Select
                                        value={selectedMarketplace}
                                        label="Marketplace (Optional)"
                                        onChange={e => setSelectedMarketplace(e.target.value as number)}
                                        disabled={isRunning}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {marketplaces.map((m) => (
                                            <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowHistoryDialog(true)}
                                        disabled={!selectedProduct || isLoadingHistory}
                                    >
                                        View History ({researchHistoryData.length})
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>2. Settings</Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <FormControl fullWidth size="small" disabled={isRunning || isLoadingPromptTemplates}>
                                        <InputLabel>
                                            Prompt Template
                                            {availablePrompts.length === 1 && selectedPromptId && ' (auto-selected)'}
                                        </InputLabel>
                                        <Select
                                            value={selectedPromptId}
                                            label="Prompt Template"
                                            onChange={e => setSelectedPromptId(e.target.value as number)}
                                        >
                                            {availablePrompts.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>
                                                    {t.name}
                                                    {t.priority === 0 && ' ⭐'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {selectedPromptTemplate && (
                                        <Tooltip title="View Prompt Details">
                                            <IconButton onClick={() => handleViewPromptTemplate(selectedPromptTemplate)}>
                                                <SearchIcon color="action" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>

                                {!isLoadingPromptTemplates && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Available prompts: {availablePrompts.length}
                                            {availablePrompts.length === 1 && ' (auto-selected)'}
                                            {availablePrompts.length > 1 && ' (choose manually)'}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>AI Service</InputLabel>
                                        <Select
                                            value={selectedAIServiceCode}
                                            label="AI Service"
                                            onChange={e => setSelectedAIServiceCode(e.target.value)}
                                            disabled={isRunning}
                                        >
                                            {aiServices.map((s) => (
                                                <MenuItem key={s.id} value={s.code}>{s.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {selectedAIService && (
                                        <Tooltip title="View AI Service Details">
                                            <IconButton onClick={() => handleViewAIService(selectedAIService)}>
                                                <SearchIcon color="action" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Button variant="contained" onClick={handleStartResearch} disabled={!selectedProduct || !selectedPromptId || isRunning}>
                                        {isRunning ? <CircularProgress size={24} color="inherit"/> : '🚀 Start Research'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {(startResearchMutation.isError || researchError) && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {(startResearchMutation.error as Error)?.message || (researchError as Error)?.message || 'An error occurred'}
                    </Alert>
                )}

                {isRunning ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column', mb: 3 }}>
                        <CircularProgress size={50} />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            {startResearchMutation.isLoading ? 'Starting research...' : 'Research in progress, fetching results...'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This might take a few moments.
                        </Typography>
                        {researchResult && (
                            <Paper sx={{p: 3, mt: 3, width: '100%'}}>
                                <Typography variant="h6" gutterBottom>Previous Research (updating...)</Typography>
                                <Box sx={{display: 'flex', gap: 2, mb: 2, alignItems: 'center'}}>
                                    <Typography>Iteration #{researchResult.iteration_number}</Typography>
                                    <Chip
                                        label={researchResult.status}
                                        color={researchResult.status === 'completed' ? 'success' : 'warning'}
                                    />
                                </Box>
                                <ResearchDetailsContent researchResult={researchResult} />
                            </Paper>
                        )}
                    </Box>
                ) : (
                    researchResult && (
                        <Paper sx={{p: 3}}>
                            <Typography variant="h5" gutterBottom>✅ Research Results</Typography>
                            <Box sx={{display: 'flex', gap: 2, mb: 2, alignItems: 'center'}}>
                                <Typography>Iteration #{researchResult.iteration_number}</Typography>
                                <Chip
                                    label={researchResult.status}
                                    color={researchResult.status === 'completed' ? 'success' : 'warning'}
                                />
                                <Typography variant="caption">
                                    Cost: ${researchResult.research_cost?.amount_usd?.toFixed(4) || 'N/A'}
                                </Typography>
                            </Box>
                            <ResearchDetailsContent researchResult={researchResult} />
                        </Paper>
                    )
                )}

                <ResearchHistoryDialog
                    open={showHistoryDialog}
                    onClose={() => setShowHistoryDialog(false)}
                    product={products.find(p => p.id === selectedProduct)}
                    historyQuery={{data: researchHistoryData, isLoading: isLoadingHistory, isError: isErrorHistory}}
                    onViewResearch={(id) => {
                        handleViewResearch(id);
                        setShowHistoryDialog(false);
                    }}
                />
                <AIGuideDialog
                    open={isAIGuideOpen}
                    onClose={() => setIsAIGuideOpen(false)}
                    aiService={viewedAIService}
                />
                <PromptViewerDialog
                    open={isPromptViewerOpen}
                    onClose={() => setIsPromptViewerOpen(false)}
                    prompt={viewedPrompt}
                />
            </>
        </Box>
    );
};

export default ResearchTestPage;